import { api } from './client'

export interface Condition {
  field: string
  op: string
  value: unknown
}

/**
 * Workflow action. Besides its type-specific params, ANY action may carry:
 *   - delayDays: int >= 0 — 0/absent runs inline, otherwise scheduled at now+delayDays
 *   - cancelOnStageChange: cancels the scheduled action if the deal leaves its stage
 */
export interface Action {
  type: string
  delayDays?: number
  cancelOnStageChange?: boolean
  [key: string]: unknown
}

/** triggerConfig of a 'date_reached' workflow (daily scan over deals). */
export interface DateReachedTriggerConfig {
  /** 'next_contact_date' or a deal custom date field key (a 'custom.' prefix is accepted too). */
  dateField: string
  /** Days after the date to fire, e.g. 365 = anniversary. Default 0. */
  offsetDays?: number
  /** Re-fire every year on the due date's anniversary. Default false. */
  repeatYearly?: boolean
}

export interface Workflow {
  id: string
  name: string
  enabled: boolean
  triggerType: string
  triggerConfig: Record<string, unknown>
  conditions: Condition[]
  actions: Action[]
}

export interface WorkflowInput {
  name: string
  enabled?: boolean
  triggerType: string
  triggerConfig?: Record<string, unknown>
  conditions?: Condition[]
  actions?: Action[]
}

export async function listWorkflows(): Promise<Workflow[]> {
  const { data } = await api.get<Workflow[]>('/api/workflows')
  return data
}

export async function createWorkflow(input: WorkflowInput): Promise<Workflow> {
  const { data } = await api.post<Workflow>('/api/workflows', input)
  return data
}

export async function updateWorkflow(id: string, input: WorkflowInput): Promise<Workflow> {
  const { data } = await api.put<Workflow>(`/api/workflows/${id}`, input)
  return data
}

export async function deleteWorkflow(id: string): Promise<void> {
  await api.delete(`/api/workflows/${id}`)
}
